import React, { useMemo } from 'react';

// Import types
import type { EventByUser, EventBlock } from '../../types/schedule';

// Import utils
import {
  formatTime,
  dateKeyFromYmd,
  hmToViewerAtYmd,
  formatColumnDate,
  formatCompactTimeFormat,
} from '../../utils/date';
import { logger } from '../../utils/logger';

/**
 * AvailabilityGrid
 *
 * Pure presentational grid that renders the time gutter, date columns, and per-cell
 * availability coloring/labels. It receives all interaction handlers and derived
 * data from hooks in the parent (useGridInteractions + useOverlapTimes) and remains
 * stateless regarding business logic.
 *
 * Key behaviors (parity with pre-refactor):
 * - Shows current-user segment labels at the first cell of each segment.
 * - Supports an "Overlapping times" overlay using counts/denominator passed from the parent.
 * - Handles pointer/mouse events by delegating to provided callbacks.
 */
export type AvailabilityGridProps = {
  // Core data
  actualYmds: string[]; // ordered list of YMD columns
  timezone: string;
  ownerTimezone?: string;
  hourMode: '12h' | '24h' | string;
  gridEnd: string; // HH:MM
  users: string[];
  currentUser: string;
  eventsByUser: EventByUser;
  isOverlapTimes: boolean;
  countsByDateKey?: Record<string, number[]>; // derived by useOverlapTimes
  totalEligible?: number; // derived by useOverlapTimes

  // Interactions from useGridInteractions
  TIMES: string[];
  canEdit: boolean;
  draftEvent: EventBlock | null;
  eraseHover: { dateKey: string; row: number } | null;
  setHoveredUsers: (v: string[]) => void;
  onDown: (
    e: React.PointerEvent<HTMLElement>,
    dateKey: string,
    row: number,
  ) => void;
  onEnter: (dateKey: string, row: number) => void;
  getUsersAtCell: (dateKey: string, row: number) => string[];

  // Tooltip handlers from the parent (kept in SchedulePage for rendering the bubble)
  startHover: (
    dateKey: string,
    row: number,
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => void;
  stopHover: () => void;
  moveHover: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  // Immediate hover callbacks to allow instant UI (e.g., who-is-available summary)
  onImmediateEnter?: (dateKey: string, row: number) => void;
  onGridLeaveImmediate?: () => void;
  // Schedule event mode
  isScheduleEventMode?: boolean;
  scheduleEventSelection?: EventBlock | null;
  // Hover state for visual feedback
  hoveredCell?: { dateKey: string; row: number } | null;
  onCellMouseEnter?: (dateKey: string, row: number) => void;
  onCellMouseLeave?: () => void;
};

function AvailabilityGrid(props: AvailabilityGridProps) {
  const {
    actualYmds,
    timezone,
    ownerTimezone,
    hourMode,
    gridEnd,
    users,
    currentUser,
    eventsByUser,
    isOverlapTimes,
    countsByDateKey,
    totalEligible,
    TIMES,
    canEdit,
    draftEvent,
    eraseHover,
    setHoveredUsers,
    onDown,
    onEnter,
    getUsersAtCell,
    startHover,
    stopHover,
    moveHover,
    isScheduleEventMode = false,
    scheduleEventSelection = null,
    hoveredCell = null,
    onCellMouseEnter,
    onCellMouseLeave,
  } = props;

  // Map dateKey -> ymd (used for labels based on viewer timezone)
  const ymdByDateKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const ymd of actualYmds) map.set(dateKeyFromYmd(ymd), ymd);
    return map;
  }, [actualYmds]);

  // Perf: Precompute a fast lookup for the current user's segment start cells.
  // Map: dateKey -> (startRow -> segment)
  const currentUserStartMap = useMemo(() => {
    const byDate = new Map<
      string,
      Map<number, { startIndex: number; endIndex: number }>
    >();
    const list = eventsByUser[currentUser] || [];
    for (const seg of list) {
      let m = byDate.get(seg.dateKey);
      if (!m) {
        m = new Map();
        byDate.set(seg.dateKey, m);
      }
      m.set(seg.startIndex, {
        startIndex: seg.startIndex,
        endIndex: seg.endIndex,
      });
    }
    return byDate;
  }, [eventsByUser, currentUser]);

  // Perf: Precompute fast membership for current user's occupied cells.
  // Map: dateKey -> Set(rows occupied by current user)
  const currentUserRowsMap = useMemo(() => {
    const byDate = new Map<string, Set<number>>();
    const list = eventsByUser[currentUser] || [];
    for (const seg of list) {
      let s = byDate.get(seg.dateKey);
      if (!s) {
        s = new Set<number>();
        byDate.set(seg.dateKey, s);
      }
      const start = Math.max(0, seg.startIndex);
      const end = Math.max(start, seg.endIndex);
      for (let r = start; r < end; r++) s.add(r);
    }
    return byDate;
  }, [eventsByUser, currentUser]);

  // Convert a HH:MM slot interpreted in the base (owner) timezone into a
  // viewer-timezone HH:MM string for a given column YMD.
  const convertHMForViewer = (ymd: string, hm: string): string => {
    const baseTz = ownerTimezone || timezone;
    return hmToViewerAtYmd(hm, ymd, baseTz, timezone);
  };

  // Helper: render label for the start of a schedule event selection
  const renderScheduleEventLabel = (dateKey: string, row: number) => {
    if (
      !scheduleEventSelection ||
      scheduleEventSelection.dateKey !== dateKey ||
      row < scheduleEventSelection.startIndex ||
      row >= scheduleEventSelection.endIndex
    ) {
      return null;
    }

    // Only render on the start cell
    if (row !== scheduleEventSelection.startIndex) {
      return null;
    }

    const ymdForDateKey = ymdByDateKey.get(dateKey);
    const startHMRaw = TIMES[scheduleEventSelection.startIndex];
    const startHM = ymdForDateKey
      ? convertHMForViewer(ymdForDateKey, startHMRaw)
      : startHMRaw;
    let startLabel = formatTime(startHM, hourMode);
    const endIdx = Math.min(scheduleEventSelection.endIndex, TIMES.length);
    const endHMRaw = endIdx < TIMES.length ? TIMES[endIdx] : gridEnd;
    const endHM = ymdForDateKey
      ? convertHMForViewer(ymdForDateKey, endHMRaw)
      : endHMRaw;
    let endLabel = formatTime(endHM, hourMode);

    // if actualYmds.length > 6, remove am / pm for space considerations
    if (actualYmds.length > 6) {
      startLabel = formatCompactTimeFormat(startLabel);
      endLabel = formatCompactTimeFormat(endLabel);
    }

    return (
      <div className="text-[11px] font-bold text-white pointer-events-none hidden sm:block">
        {startLabel} - {endLabel}
      </div>
    );
  };

  // Helper: render label for the start of a current-user segment (and during draft)
  const renderEventLabel = (dateKey: string, row: number) => {
    // Consider a draft event first
    if (
      draftEvent &&
      draftEvent.dateKey === dateKey &&
      row >= draftEvent.startIndex &&
      row < draftEvent.endIndex
    ) {
      // Compute start/end labels honoring timezone and hour mode
      const ymdForDateKey = ymdByDateKey.get(dateKey);
      const startHMRaw = TIMES[draftEvent.startIndex];
      const startHM = ymdForDateKey
        ? convertHMForViewer(ymdForDateKey, startHMRaw)
        : startHMRaw;
      let startLabel = formatTime(startHM, hourMode);
      const endIdx = Math.min(draftEvent.endIndex, TIMES.length);
      const endHMRaw = endIdx < TIMES.length ? TIMES[endIdx] : gridEnd;
      const endHM = ymdForDateKey
        ? convertHMForViewer(ymdForDateKey, endHMRaw)
        : endHMRaw;
      let endLabel = formatTime(endHM, hourMode);

      // if actualYmds.length > 6, remove am / pm for space considerations
      if (actualYmds.length > 6) {
        startLabel = formatCompactTimeFormat(startLabel);
        endLabel = formatCompactTimeFormat(endLabel);
      }

      // const ymdForDateKey = ymdByDateKey.get(dateKey);

      // const dateDisplay = ymdForDateKey
      //   ? formatColumnDate(ymdForDateKey, 'MMM D')
      //   : new Date(dateKey).toLocaleDateString(undefined, {
      //       month: 'short',
      //       day: 'numeric',
      //       timeZone: timezone,
      //     });

      if (row === draftEvent.startIndex) {
        // {dateDisplay}: {startLabel} - {endLabel}
        return (
          <div className="text-[11px] font-bold text-emerald-900 pointer-events-none hidden sm:block">
            {startLabel} - {endLabel}
          </div>
        );
      }
    }

    // O(1) lookup: only render on the start cell of a segment
    const seg = currentUserStartMap.get(dateKey)?.get(row);
    if (!seg) return null; // not the start cell
    const ymdForDateKey = ymdByDateKey.get(dateKey);
    const startHMRaw = TIMES[seg.startIndex];
    const startHM = ymdForDateKey
      ? convertHMForViewer(ymdForDateKey, startHMRaw)
      : startHMRaw;
    let startLabel = formatTime(startHM, hourMode);
    const endIdx = Math.min(seg.endIndex, TIMES.length);
    const endHMRaw = endIdx < TIMES.length ? TIMES[endIdx] : gridEnd;
    const endHM = ymdForDateKey
      ? convertHMForViewer(ymdForDateKey, endHMRaw)
      : endHMRaw;
    let endLabel = formatTime(endHM, hourMode);

    // if actualYmds.length > 6, remove am / pm for space considerations
    if (actualYmds.length > 6) {
      startLabel = formatCompactTimeFormat(startLabel);
      endLabel = formatCompactTimeFormat(endLabel);
    }

    return (
      <div className="text-[11px] font-bold text-emerald-900 pointer-events-none hidden sm:block">
        {startLabel} - {endLabel}
      </div>
    );
  };

  // Helper: render overlapping-times label for the start of a contiguous overlap span
  const renderOverlapLabel = (dateKey: string, row: number) => {
    // Overlapping-times time range label at start of contiguous span
    const ymdForDateKey = ymdByDateKey.get(dateKey);
    const startHMRaw = TIMES[row];
    const endHMRaw = row + 1 < TIMES.length ? TIMES[row + 1] : gridEnd;
    const startHM = ymdForDateKey
      ? convertHMForViewer(ymdForDateKey, startHMRaw)
      : startHMRaw;
    const endHM = ymdForDateKey
      ? convertHMForViewer(ymdForDateKey, endHMRaw)
      : endHMRaw;
    let startLabel = formatTime(startHM, hourMode);
    let endLabel = formatTime(endHM, hourMode);

    // if actualYmds.length > 6, remove am / pm for space considerations
    if (actualYmds.length > 6) {
      startLabel = formatCompactTimeFormat(startLabel);
      endLabel = formatCompactTimeFormat(endLabel);
    }

    return (
      <div className="text-[11px] font-bold text-white pointer-events-none hidden sm:block">
        {startLabel} - {endLabel}
      </div>
    );
  };

  // If not provided, fall back to computing denominator like before (back-compat)
  const fallbackEligible = useMemo(
    () => users.filter((u) => (eventsByUser[u] || []).length > 0).length,
    [users, eventsByUser],
  );

  // Loading state: show a placeholder grid while we fetch data
  if (!actualYmds)
    return (
      <div className="h-full w-full flex items-center justify-center">
        Loading...
      </div>
    );

  return (
    <div
      className="grid bg-white border rounded-lg shadow-lg overflow-hidden"
      style={{
        gridTemplateColumns: '60px repeat(' + actualYmds.length + ', 1fr)',
        // Ensure mobile browsers deliver pointer events during touch drag (no page scroll/zoom)
      }}
      // Clear hover states only when the pointer leaves the entire grid to avoid
      // flicker while moving between adjacent cells
      onMouseLeave={() => {
        stopHover();
        setHoveredUsers([]);
        onCellMouseLeave?.();
        // Inform parent to clear any immediate-hover summaries
        try {
          props.onGridLeaveImmediate?.();
        } catch (e) {
          logger.error('Failed to notify parent of immediate-hover clear', e);
        }
      }}>
      {/* Header row */}
      <div className="border-r h-12 flex items-center justify-center text-xs font-bold text-gray-500">
        TIME
      </div>
      {actualYmds.map((ymd) => {
        const headerKey = dateKeyFromYmd(ymd);
        const baseTz = ownerTimezone || timezone;
        const weekday = formatColumnDate(ymd, 'ddd', baseTz, timezone);
        const monthDay = formatColumnDate(ymd, 'MMM D', baseTz, timezone);
        return (
          <div
            key={headerKey}
            className="border-r h-12 flex flex-col items-center justify-center text-xs font-bold text-gray-600">
            {weekday}
            <span className="text-gray-900 font-bold">{monthDay}</span>
          </div>
        );
      })}

      {/* Time labels column (follow viewer timezone; convert from owner's configured window) */}
      <div className="border-r border-t">
        {(() => {
          const refYmd = actualYmds[0];
          const hasOwnerTz = !!ownerTimezone;
          return TIMES.map((t) => {
            let labelHM = t;
            if (hasOwnerTz) {
              try {
                labelHM = hmToViewerAtYmd(t, refYmd, ownerTimezone!, timezone);
              } catch (e: unknown) {
                logger.error(
                  'Failed to convert time to viewer timezone:',
                  t,
                  e,
                );
              }
            }

            if (hourMode === '12h') {
              const h = Number(labelHM.split(':')[0]);
              const m = Number(labelHM.split(':')[1]);
              const ampm = h < 12 ? 'AM' : 'PM';
              const hh12 = h % 12 === 0 ? 12 : h % 12;
              return (
                <div
                  key={t}
                  className="h-4 border-b border-dotted border-gray-200 text-[11px] font-semibold text-gray-600 flex items-center justify-center">
                  {hh12}:{String(m).padStart(2, '0')} {ampm}
                </div>
              );
            }

            return (
              <div
                key={t}
                className="h-4 border-b border-dotted border-gray-200 text-[11px] font-semibold text-gray-600 flex items-center justify-center">
                {labelHM}
              </div>
            );
          });
        })()}
      </div>

      {/* Date columns */}
      {actualYmds.map((ymd) => {
        const dateKey = dateKeyFromYmd(ymd);
        const denom =
          typeof totalEligible === 'number' ? totalEligible : fallbackEligible; // denominator for overlapping-times
        return (
          <div
            key={dateKey}
            className="border-r border-t"
            // Column-level pointer move helps mobile where per-cell pointerenter/move can be skipped
            onPointerMove={(e) => {
              // Find the cell under the pointer and synthesize an entering for it
              const el = document.elementFromPoint(
                e.clientX,
                e.clientY,
              ) as HTMLElement | null;
              if (!el) return;
              const cell = el.closest('[data-cell="1"]') as HTMLElement | null;
              if (!cell) return;
              const dk = cell.dataset.dateKey;
              const r = cell.dataset.row;
              if (dk && r) {
                onEnter(dk, Number(r));
              }
            }}>
            {TIMES.map((t, row) => {
              // O(1) check: does current user occupy this cell?
              const currentHas =
                currentUserRowsMap.get(dateKey)?.has(row) ?? false;
              const underErase =
                eraseHover &&
                eraseHover.dateKey === dateKey &&
                eraseHover.row === row;

              // Per-cell count from derived data (fallback to inline computation if absent)
              let count: number;
              if (countsByDateKey && countsByDateKey[dateKey]) {
                count = countsByDateKey[dateKey][row] || 0;
              } else {
                let c = 0;
                for (const u of users) {
                  const list = eventsByUser[u] || [];
                  if (
                    list.some(
                      (s) =>
                        s.dateKey === dateKey &&
                        row >= s.startIndex &&
                        row < s.endIndex,
                    )
                  )
                    c++;
                }
                count = c;
              }

              // Basic presence flag used in normal (non-overlapping-times) mode
              const hasAny = count > 0;

              // In Overlapping Times mode, we now color:
              // - Dark green: full overlap (everyone available)
              // - Soft green: partial overlap (>= 2 available, but not everyone)
              // - Yellow: no overlap (exactly 1 available)
              const threshold = 2; // partial overlap threshold
              const hasPartialOrMore = count >= threshold;
              const isSingleAvail = count === 1; // show as yellow
              const isOverlapCell =
                isOverlapTimes && denom > 0 && count === denom;
              // Any heatmap cell when Overlapping Times is ON that meets the threshold or is single availability
              const isHeatCell =
                isOverlapTimes && (hasPartialOrMore || isSingleAvail);

              // Draft highlight while dragging (green hover)
              const isDraftCell =
                !isOverlapTimes &&
                !!draftEvent &&
                draftEvent.dateKey === dateKey &&
                row >= draftEvent.startIndex &&
                row < draftEvent.endIndex;

              // Schedule event selection highlight
              const isScheduleEventCell =
                isScheduleEventMode &&
                !!scheduleEventSelection &&
                scheduleEventSelection.dateKey === dateKey &&
                row >= scheduleEventSelection.startIndex &&
                row < scheduleEventSelection.endIndex;

              let finalBg = '';
              if (isOverlapTimes) {
                // Heatmap: best overlap (all) = dark-green, partial (>=2) = soft green, single (1) = yellow, none = white
                if (isOverlapCell) finalBg = ' bg-emerald-400';
                else if (hasPartialOrMore) finalBg = ' bg-emerald-100';
                else if (isSingleAvail) finalBg = ' bg-amber-100';
              } else {
                // Show the draft range as green while dragging; otherwise current-user commitments are green
                if (isDraftCell || currentHas) {
                  finalBg = ' bg-emerald-100';
                } else if (hasAny) {
                  finalBg = ' bg-gray-100';
                }
              }

              // Schedule event mode: highlight selected cells with blue
              if (isScheduleEventCell) {
                finalBg = ' bg-blue-500';
              }

              // Hover effect: add subtle highlight on hover using outline (renders on top)
              const isHoveredCell =
                hoveredCell &&
                hoveredCell.dateKey === dateKey &&
                hoveredCell.row === row;

              let hoverOutline = '';
              // Hover outline logic:
              // - In normal mode with current user, always show green outline on hover (if not overlapping-times)
              // - In normal mode with other users, always show gray outline on hover (if not overlapping-times)
              // - In overlapping-times mode, show gray hover outline on any non-selected cell
              // - In overlapping-times with schedule-event mode, show blue hover outline on any non-selected cell
              // - In schedule-event mode, show blue hover outline on any non-selected cell
              // - If under erase, always show red outline (overrides others)

              // Don't show hover outline if under erase (red outline will override)
              if (!underErase) {
                if (isScheduleEventMode) {
                  // Schedule event mode: blue outline on any non-selected cell
                  hoverOutline = ' hover:outline hover:outline-2 hover:outline-blue-300 hover:outline-offset-[-2px]';
                } else if (isOverlapTimes) {
                  // Overlapping-times mode: gray outline on any non-selected cell
                  hoverOutline = ' hover:outline hover:outline-2 hover:outline-gray-300 hover:outline-offset-[-2px]';
                } else {
                  // Normal mode (not overlapping-times)
                  if (isHoveredCell && !isDraftCell && !isScheduleEventCell) {
                    if (currentHas) {
                      // Current user's cell: green outline
                      hoverOutline = ' outline outline-2 outline-green-300 outline-offset-[-2px]';
                    } else {
                      // Other users' cells: gray outline
                      finalBg = finalBg || ' bg-gray-50';
                      hoverOutline = ' outline outline-2 outline-gray-300 outline-offset-[-2px]';
                    }
                  } else if ((isDraftCell || currentHas) && !isOverlapTimes) {
                    // Current user's draft or existing cell: green hover outline
                    hoverOutline = ' hover:outline hover:outline-2 hover:outline-green-300 hover:outline-offset-[-2px]';
                  }
                }
              }

              // Red outline when under erase hover
              const outline = underErase
                ? ' outline outline-2 outline-red-400 outline-offset-[-2px]'
                : '';

              return (
                <div
                  key={t}
                  data-cell="1"
                  data-date-key={dateKey}
                  data-row={row}
                  onPointerDown={(e) => {
                    // Prevent page scroll on touch devices so dragging across cells works
                    if ((e as React.PointerEvent).pointerType === 'touch') {
                      e.preventDefault();
                    }
                    onDown(e, dateKey, row);
                  }}
                  onPointerEnter={() => {
                    onEnter(dateKey, row);
                    // In Overlapping Times, highlight participants over any heatmap cell (amber or green)
                    if (isHeatCell)
                      setHoveredUsers(getUsersAtCell(dateKey, row));
                    else setHoveredUsers([]);
                    // Notify parent immediately for instant summaries
                    try {
                      props.onImmediateEnter?.(dateKey, row);
                    } catch {
                      logger.warn(
                        'Failed to notify parent of immediate-hover summary',
                      );
                    }
                  }}
                  // On mobile, pointerenter may not fire while sliding across siblings.
                  // Trigger the same logic during pointer moves.
                  onPointerMove={() => {
                    onEnter(dateKey, row);
                  }}
                  onMouseEnter={(e) => {
                    // Always allow tooltips (mobile suppression handled in SchedulePage.startHover)
                    startHover(dateKey, row, e);

                    // Participant hover highlight (only for heatmap cells in Overlapping Times)
                    if (isHeatCell)
                      setHoveredUsers(getUsersAtCell(dateKey, row));
                    else setHoveredUsers([]);
                    // Also notify parent immediately (mouse paths that don't fire pointer events)
                    try {
                      props.onImmediateEnter?.(dateKey, row);
                    } catch {
                      logger.warn(
                        'Failed to notify parent of immediate-hover summary',
                      );
                    }
                    // Track hover for visual effect
                    onCellMouseEnter?.(dateKey, row);
                  }}
                  // Clear the tooltip immediately when leaving a cell so it disappears right away.
                  onMouseLeave={() => {
                    stopHover();
                    onCellMouseLeave?.();
                  }}
                  onMouseMove={moveHover}
                  className={
                    'relative h-4 border-b border-dotted border-gray-200 touch-none select-none ' +
                    (canEdit || isScheduleEventMode ? 'cursor-pointer ' : 'cursor-not-allowed ') +
                    finalBg +
                    outline +
                    hoverOutline
                  }>
                  {isOverlapCell && renderOverlapLabel(dateKey, row)}
                  {/* Schedule event label (on start cell) */}
                  {isScheduleEventCell &&
                    row === scheduleEventSelection?.startIndex &&
                    renderScheduleEventLabel(dateKey, row)}
                  {/* Event time and date label (current user only, on start cell, only in normal mode) */}
                  {/* Show label for existing segments or during drag (draftEvent) */}
                  {!isOverlapTimes && (currentHas || (draftEvent && draftEvent.dateKey === dateKey && row === draftEvent.startIndex)) && renderEventLabel(dateKey, row)}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export default AvailabilityGrid;
